import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Newspaper, RefreshCw, Sparkles, Trash2, Eye, ExternalLink, Globe, Flag, Plus, Save, CarFront, Battery, Cpu, Factory, BookOpen, ShieldCheck, Leaf } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RUBRICHE = [
  { value: "noleggio",     label: "Noleggio & Flotte",     Icon: CarFront },
  { value: "novita_motor", label: "Novità Modelli",        Icon: Sparkles },
  { value: "elettrico",    label: "Elettrico & Ibrido",    Icon: Battery },
  { value: "tecnologia",   label: "Tecnologia & ADAS",     Icon: Cpu },
  { value: "industria",    label: "Industria & Mercato",   Icon: Factory },
  { value: "guide",        label: "Guide & Consigli",      Icon: BookOpen },
  { value: "sicurezza",    label: "Sicurezza Stradale",    Icon: ShieldCheck },
  { value: "green",        label: "Mobilità Sostenibile",  Icon: Leaf },
];

const EMPTY = {
  titolo: "", rubrica: "novita_motor", sommario: "",
  contenuto_markdown: "", commento_colucci: "", suggerimento: "",
  fonte_nome: "Redazione Colucci Solutions", fonte_link: "",
  immagine_url: "", tag: "",
};

export default function NewsNoleggioTab() {
  const [data, setData] = useState({ articoli: [], updated_at: null, fonti_consultate: [] });
  const [fonti, setFonti] = useState({ internazionali: [], italiane: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [resNews, resFonti] = await Promise.all([
        axios.get(`${API}/news-noleggio`, { withCredentials: true }),
        axios.get(`${API}/admin/news-noleggio/fonti`, { withCredentials: true }),
      ]);
      setData(resNews.data);
      setFonti(resFonti.data);
    } catch (e) {
      toast.error("Errore caricamento");
      console.warn("[NewsNLT admin]", e?.message || e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refreshNow = async () => {
    if (!confirm("Rigenerare tutti gli articoli AI dalle 13 fonti RSS? Il processo dura 60-90 secondi in background.")) return;
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/admin/news-noleggio/refresh`, {}, { withCredentials: true });
      if (r.data.status === "running") {
        toast.info(r.data.message || "Job già in corso");
      } else {
        toast.success("Generazione avviata in background");
      }
      // Poll status
      const pollInterval = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/admin/news-noleggio/refresh-status`, { withCredentials: true });
          if (s.data.status === "done") {
            clearInterval(pollInterval);
            setRefreshing(false);
            toast.success(`✓ ${s.data.generati} articoli generati da ${s.data.raw_items} RSS`);
            fetchAll();
          } else if (s.data.status === "error") {
            clearInterval(pollInterval);
            setRefreshing(false);
            toast.error(`Errore: ${s.data.error}`);
          }
        } catch { /* keep polling */ }
      }, 5000);
      // Safety stop dopo 5 min
      setTimeout(() => { clearInterval(pollInterval); setRefreshing(false); }, 300000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore avvio refresh");
      setRefreshing(false);
    }
  };

  const removeArt = async (slug) => {
    if (!confirm("Eliminare questo articolo?")) return;
    try {
      await axios.delete(`${API}/admin/news-noleggio/articolo/${slug}`, { withCredentials: true });
      toast.success("Articolo eliminato");
      fetchAll();
    } catch { toast.error("Errore"); }
  };

  const saveManual = async () => {
    if (!form.titolo || !form.contenuto_markdown) { toast.error("Titolo e contenuto obbligatori"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/news-noleggio/articolo`, form, { withCredentials: true });
      toast.success("Articolo aggiunto");
      setOpenAdd(false);
      setForm(EMPTY);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const updated = data.updated_at ? new Date(data.updated_at).toLocaleString("it-IT") : "";
  const countByRub = data.articoli.reduce((acc, a) => { acc[a.rubrica] = (acc[a.rubrica] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="news-noleggio-tab">
      {/* Header + stats */}
      <div className="bg-white rounded-xl border border-zinc-200 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <h3 className="font-heading text-base font-semibold text-[#0F1F14] flex items-center gap-2"><Newspaper className="w-4 h-4 text-[#D97B58]" /> Digest Motor & Noleggio</h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              Articoli aggregati da <strong>{fonti.internazionali.length + fonti.italiane.length} testate</strong> ({fonti.italiane.length} IT + {fonti.internazionali.length} EN), rielaborati da Claude Sonnet 4.5 con commenti/suggerimenti Colucci.
            </p>
            {updated && <p className="text-[10px] text-zinc-400 mt-1">Ultimo aggiornamento: {updated}</p>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setOpenAdd(true)} className="h-9 text-xs gap-1.5" data-testid="add-manual-article">
              <Plus className="w-3.5 h-3.5" /> Articolo manuale
            </Button>
            <Button onClick={refreshNow} disabled={refreshing} size="sm" className="h-9 text-xs bg-[#D97B58] hover:bg-[#C06A48] text-white gap-1.5" data-testid="refresh-ai-btn">
              <Sparkles className="w-3.5 h-3.5" /> {refreshing ? "Generazione AI..." : "Genera articoli AI"}
            </Button>
          </div>
        </div>
        {/* Count per rubrica */}
        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-zinc-100 flex-wrap">
          {RUBRICHE.map(r => (
            <div key={r.value} className="flex items-center gap-2 bg-zinc-50 rounded-lg px-3 py-1.5">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">{r.label}</span>
              <span className="text-xs font-bold text-[#163321]">{countByRub[r.value] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Fonti */}
      <details className="bg-white rounded-xl border border-zinc-200 p-5">
        <summary className="text-xs font-semibold text-[#0F1F14] cursor-pointer flex items-center gap-2">
          <Globe className="w-3.5 h-3.5" /> Fonti RSS monitorate ({fonti.internazionali.length + fonti.italiane.length})
        </summary>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-[11px]">
          <div>
            <p className="font-bold text-[#0F1F14] mb-2 flex items-center gap-1"><Globe className="w-3 h-3" /> Internazionali</p>
            <ul className="space-y-1">
              {fonti.internazionali.map(f => <li key={f.domain} className="flex items-center gap-1 text-zinc-600"><span className="w-1 h-1 bg-[#D97B58] rounded-full" />{f.nome}</li>)}
            </ul>
          </div>
          <div>
            <p className="font-bold text-[#0F1F14] mb-2 flex items-center gap-1"><Flag className="w-3 h-3" /> Italiane</p>
            <ul className="space-y-1">
              {fonti.italiane.map(f => <li key={f.domain} className="flex items-center gap-1 text-zinc-600"><span className="w-1 h-1 bg-[#163321] rounded-full" />{f.nome}</li>)}
            </ul>
          </div>
        </div>
      </details>

      {/* Articoli */}
      {loading ? (
        <div className="flex items-center justify-center py-14">
          <div className="w-8 h-8 border-2 border-[#163321] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data.articoli.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-zinc-200" data-testid="news-empty">
          <Newspaper className="w-10 h-10 mx-auto text-zinc-300 mb-2" />
          <p className="text-sm text-zinc-600 font-semibold">Nessun articolo</p>
          <p className="text-xs text-zinc-500">Clicca <strong>"Genera articoli AI"</strong> per creare il primo digest.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-3 text-left">Titolo</th>
                <th className="px-4 py-3 text-left">Rubrica</th>
                <th className="px-4 py-3 text-left">Fonte</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {data.articoli.map(a => {
                const rub = RUBRICHE.find(r => r.value === a.rubrica)?.label || a.rubrica;
                const isIT = a.fonte_nome && !/(UK|US|FR|DE)/i.test(a.fonte_nome);
                return (
                  <tr key={a.slug} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`news-row-${a.slug}`}>
                    <td className="px-4 py-3 max-w-[320px]">
                      <div className="flex items-start gap-2">
                        {a.immagine_url && <img src={a.immagine_url} alt="" className="w-10 h-10 object-cover rounded shrink-0" loading="lazy" onError={e => { e.currentTarget.style.display = "none"; }} />}
                        <div className="min-w-0">
                          <p className="font-medium text-zinc-900 truncate text-xs">{a.titolo}</p>
                          {a.manual && <span className="text-[9px] font-bold uppercase text-[#D97B58]">Manuale</span>}
                          {a.sommario && <p className="text-[10px] text-zinc-500 truncate mt-0.5">{a.sommario}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-zinc-100 text-zinc-700`}>
                        {(() => {
                          const R = RUBRICHE.find(r => r.value === a.rubrica);
                          const I = R?.Icon || Sparkles;
                          return <I className="w-2.5 h-2.5" />;
                        })()}
                        {rub}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[10px] text-zinc-600">
                      <span className="inline-flex items-center gap-1">
                        {isIT ? <Flag className="w-2.5 h-2.5" /> : <Globe className="w-2.5 h-2.5" />}
                        {a.fonte_nome || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {a.fonte_link && <a href={a.fonte_link} target="_blank" rel="noopener noreferrer" className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-zinc-100" title="Fonte originale"><ExternalLink className="w-3.5 h-3.5 text-zinc-600" /></a>}
                        <a href={`/noleggio/novita/${a.slug}`} target="_blank" rel="noopener noreferrer" className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-zinc-100" title="Anteprima"><Eye className="w-3.5 h-3.5 text-zinc-600" /></a>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => removeArt(a.slug)} title="Elimina" data-testid={`delete-${a.slug}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog aggiungi manuale */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Nuovo articolo manuale</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <Label className="text-xs">Titolo *</Label>
              <Input value={form.titolo} onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} maxLength={200} data-testid="form-titolo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Rubrica</Label>
                <Select value={form.rubrica} onValueChange={v => setForm(f => ({ ...f, rubrica: v }))}>
                  <SelectTrigger data-testid="form-rubrica"><SelectValue /></SelectTrigger>
                  <SelectContent>{RUBRICHE.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tag (opzionale)</Label>
                <Input value={form.tag} onChange={e => setForm(f => ({ ...f, tag: e.target.value }))} placeholder="EV, flotte, Arval" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Sommario (max 500 char)</Label>
              <textarea value={form.sommario} onChange={e => setForm(f => ({ ...f, sommario: e.target.value }))} maxLength={500} className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm min-h-[60px]" />
            </div>
            <div>
              <Label className="text-xs">Contenuto (markdown) *</Label>
              <textarea value={form.contenuto_markdown} onChange={e => setForm(f => ({ ...f, contenuto_markdown: e.target.value }))} className="w-full rounded-md border border-zinc-200 px-3 py-2 text-xs font-mono min-h-[200px]" data-testid="form-contenuto" />
            </div>
            <div>
              <Label className="text-xs">Commento Colucci Solutions (60-100 parole)</Label>
              <textarea value={form.commento_colucci} onChange={e => setForm(f => ({ ...f, commento_colucci: e.target.value }))} className="w-full rounded-md border border-zinc-200 px-3 py-2 text-xs min-h-[70px]" />
            </div>
            <div>
              <Label className="text-xs">Suggerimento (30-50 parole)</Label>
              <textarea value={form.suggerimento} onChange={e => setForm(f => ({ ...f, suggerimento: e.target.value }))} className="w-full rounded-md border border-zinc-200 px-3 py-2 text-xs min-h-[50px]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fonte nome</Label>
                <Input value={form.fonte_nome} onChange={e => setForm(f => ({ ...f, fonte_nome: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Fonte link (https://)</Label>
                <Input value={form.fonte_link} onChange={e => setForm(f => ({ ...f, fonte_link: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">URL immagine</Label>
              <Input value={form.immagine_url} onChange={e => setForm(f => ({ ...f, immagine_url: e.target.value }))} placeholder="https://..." />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-zinc-100">
            <Button variant="outline" size="sm" onClick={() => setOpenAdd(false)}>Annulla</Button>
            <Button onClick={saveManual} disabled={saving} size="sm" className="bg-[#163321] hover:bg-[#1d4a2e] text-white gap-1.5" data-testid="save-manual">
              <Save className="w-3.5 h-3.5" /> {saving ? "..." : "Salva"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
